import React, { useEffect, useState, useCallback } from 'react';
import { withStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import CustomTable from './components/CustomTable';
import { useCallManager } from './CallManagerContext';
import {
  getVapiActiveCalls,
  getVapiCallHistory,
  getVapiFlaggedCalls,
  flagCall,
  getVapiFailedCalls,
  exportCallHistory,
  exportNeedsAttention,
  exportCallQueue,
  getCallMetrics,
} from '../../services/vapi.service';
import { useHistory } from 'react-router-dom';
import { Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Box, CircularProgress } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { callMonitor } from '../../services/callMonitor.service';
import classnames from 'classnames';
import GetAppIcon from '@material-ui/icons/GetApp';
import { format } from 'date-fns';

const getTodayDateString = () => format(new Date(), 'yyyy-MM-dd');

const styles = theme => ({
  card: {
    margin: 20,
    minWidth: 275,
  },
  actionButton: {
    marginRight: 8,
    textTransform: 'none',
  },
  monitorButton: {
    backgroundColor: '#3a87ad',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2a6d8d',
    },
  },
  endButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#bd2130',
    },
  },
  flagButton: {
    backgroundColor: '#F0AD4E',
    color: 'white',
    '&:hover': {
      backgroundColor: '#eda642',
    },
  },
  viewButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    '&:hover': {
      backgroundColor: '#5a6268',
    },
  },
  outcomeChip: {
    borderRadius: 16,
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  outcomeIncomplete: {
    backgroundColor: '#fff3e0',
    color: '#f57c00',
    border: '1px solid #ffcc80',
  },
  outcomeUnsuccessful: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: '1px solid #ef9a9a',
  },
  exportButton: {
    backgroundColor: '#3a87ad',
    color: 'white',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#2a6d8d',
    },
  },
});

const completedColumns = [
  { id: 'orderLineId', label: 'Order Ticket', sortable: true, sortId: 'orderLineId' },
  { id: ['policyHolder.fname', 'policyHolder.lname'], label: 'Insured Name', sortable: true, sortId: 'customername' },
  { id: ['clientName'], label: 'Client' },
  { id: ['policyHolder.phoneNumber'], label: 'Phone' },
  { id: 'policyNumber', label: 'Policy', sortable: true, sortId: 'policynumber' },
  { id: 'callDateTime', label: 'Time', type: 'date', sortable: true, sortId: 'calldatetime' },
  { id: 'callDurationSeconds', label: 'Duration', type: 'time', sortable: true, sortId: 'calldurationseconds' },
  { id: 'successEvaluation', label: 'Outcome', type: 'outcome' },
];

const flaggedColumns = [
  { id: 'orderId', label: 'Order Ticket', sortable: true, sortId: 'orderid' },
  { id: ['customer.fname', 'customer.lname'], label: 'Customer', sortable: true, sortId: 'customername' },
  { id: 'phoneNumber', label: 'Phone' },
  { id: 'duration', label: 'Duration', type: 'time', sortable: true, sortId: 'duration' },
  { id: 'callDateTime', label: 'Time', type: 'date', sortable: true, sortId: 'calldatetime' },
];

const needsAttentionColumns = [
  { id: 'orderLineId', label: 'Order Ticket', sortable: true, sortId: 'orderLineId' },
  { id: ['policyHolder.fname', 'policyHolder.lname'], label: 'Insured Name', sortable: true, sortId: 'customername' },
  { id: 'clientName', label: 'Client', sortable: true, sortId: 'clientname' },
  { id: ['policyHolder.phoneNumber'], label: 'Phone' },
  { id: 'policyNumber', label: 'Policy', sortable: true, sortId: 'policynumber' },
  { id: 'callDateTime', label: 'Time', type: 'date', sortable: true, sortId: 'calldatetime' },
  { id: 'callDurationSeconds', label: 'Duration', type: 'time', sortable: true, sortId: 'calldurationseconds' },
  { id: 'successEvaluation', label: 'Outcome', type: 'outcome' },
];

export const QueueTab = withStyles(styles)(
  ({
    classes,
    columns,
    data,
    actionButtons,
    title,
    loading,
    pageSize,
    totalCount,
    page,
    setPage,
    setPageSize,
    fetchCallList,
  }) => {
    const [search, setSearch] = useState('');
    const [exporting, setExporting] = useState(false);

    const handleSearch = useCallback(value => {
      setSearch(value);
      setPage(0);
      fetchCallList({ page: 0, pageSize, search: value });
    }, [fetchCallList, pageSize, setPage]);

    const handleExport = async () => {
      setExporting(true);
      try {
        await exportCallQueue();
      } catch (error) {
      } finally {
        setExporting(false);
      }
    };

    return (
      <CustomTable
        columns={columns}
        data={data}
        actionButtons={actionButtons}
        title={title}
        loading={loading}
        allowRowClick={false}
        pagination
        pageSize={pageSize}
        totalCount={totalCount}
        page={page}
        allowSearch={true}
        onSearch={handleSearch}
        onPageChange={newPage => {
          setPage(newPage);
          fetchCallList({ page: newPage, pageSize, search });
        }}
        onPageSizeChange={newPageSize => {
          setPageSize(newPageSize);
          setPage(0);
          fetchCallList({ page: 0, pageSize: newPageSize, search });
        }}
        headerActions={
          <Button
            variant="contained"
            size="small"
            className={classes.exportButton}
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        }
      />
    );
  },
);

export const CompletedCallsTab = withStyles(styles)(({ classes }) => {
  const [callHistoryList, setCallHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const history = useHistory();

  const fetchCallHistoryList = useCallback(
    async (fetchPage, fetchPageSize, fetchSearch, fetchSortColumn, fetchSortDirection) => {
      setLoading(true);
      try {
        const data = await getVapiCallHistory({
          page: fetchPage + 1,
          pageSize: fetchPageSize,
          search: fetchSearch,
          sort: fetchSortColumn,
          order: fetchSortDirection,
        });
        setCallHistoryList(data.results || []);
        setTotalCount(data.totalCount || 0);
      } catch (error) {
        console.error('Error fetching call history list:', error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const ViewButton = ({ row }) => (
    <Button
      variant="contained"
      size="small"
      className={`${classes.actionButton} ${classes.viewButton}`}
      onClick={() => history.push(`/call-manager/analysis/${row.providerCallId}`)}
    >
      VIEW
    </Button>
  );

  useEffect(() => {
    fetchCallHistoryList(page, pageSize, search, sortColumn, sortDirection);
  }, [page, pageSize, search, sortColumn, sortDirection, fetchCallHistoryList]);

  const handleSearch = useCallback(value => {
    setSearch(value);
    setPage(0);
  }, []);

  const handlePageChange = useCallback(newPage => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback(newPageSize => {
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  console.log({callHistoryList});

  const handleSort = useCallback(
    columnId => {
      const isAsc = sortColumn === columnId && sortDirection === 'asc';
      setSortDirection(isAsc ? 'desc' : 'asc');
      setSortColumn(columnId);
      setPage(0);
    },
    [sortColumn, sortDirection],
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const start = exportStartDate ? new Date(exportStartDate).toISOString() : null;
      const end = exportEndDate ? new Date(exportEndDate + 'T23:59:59').toISOString() : null;
      await exportCallHistory(start, end);
      setExportDialogOpen(false);
    } catch (error) {
      if (error.message === 'No records to export') alert('No records found for the selected date range.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
    <CustomTable
      columns={completedColumns}
      data={callHistoryList}
      actionButtons={[
        {
          name: 'ViewButton',
          component: ViewButton,
        },
      ]}
      title="Call History"
      chipClasses={{
        outcome: {
          Success: classes.outcomeIncomplete,
          Failed: classes.outcomeUnsuccessful,
        },
      }}
      pagination
      pageSize={pageSize}
      totalCount={totalCount}
      page={page}
      loading={loading}
      allowRowClick={false}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onSearch={handleSearch}
      onSort={handleSort}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      headerActions={
        <Button
          variant="contained"
          size="small"
          className={classes.exportButton}
          startIcon={<GetAppIcon />}
          onClick={() => setExportDialogOpen(true)}
        >
          Export
        </Button>
      }
    />
    <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
      <DialogTitle>Export Call History</DialogTitle>
      <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 300 }}>
        <TextField
          label="Start Date"
          type="date"
          value={exportStartDate}
          onChange={e => setExportStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="End Date"
          type="date"
          value={exportEndDate}
          onChange={e => setExportEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <Typography variant="caption" color="textSecondary">
          Leave both blank to export all records.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleExport} color="primary" variant="contained" disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
});

// export const FlaggedCallsTab = withStyles(styles)(({ classes }) => {
//   const [loading, setLoading] = useState(false);
//   const [flaggedCallsList, setFlaggedCallsList] = useState([]);
//   const [totalCount, setTotalCount] = useState(0);
//   const [page, setPage] = useState(0);
//   const [pageSize, setPageSize] = useState(10);
//   const [search, setSearch] = useState('');
//   const [sortColumn, setSortColumn] = useState(null);
//   const [sortDirection, setSortDirection] = useState('asc');

//   const history = useHistory();

//   const fetchFlaggedCallsList = useCallback(
//     async (fetchPage, fetchPageSize, fetchSearch, fetchSortColumn, fetchSortDirection) => {
//       setLoading(true);
//       try {
//         const data = await getVapiFlaggedCalls({
//           page: fetchPage + 1,
//           pageSize: fetchPageSize,
//           search: fetchSearch,
//           sort: fetchSortColumn,
//           order: fetchSortDirection,
//         });

//         setFlaggedCallsList(data.results || []);
//         setTotalCount(data.totalCount || 0);
//       } catch (error) {
//         console.error('Error fetching flagged calls list:', error);
//         setLoading(false);
//       } finally {
//         setLoading(false);
//       }
//     },
//     [],
//   );

//   const ViewButton = ({ row }) => (
//     <Button
//       variant="contained"
//       size="small"
//       className={`${classes.actionButton} ${classes.viewButton}`}
//       onClick={() => history.push(`/call-manager/analysis/${row.providerCallId}`)}
//     >
//       VIEW
//     </Button>
//   );

//   useEffect(() => {
//     fetchFlaggedCallsList(page, pageSize, search, sortColumn, sortDirection);
//   }, [page, pageSize, search, sortColumn, sortDirection, fetchFlaggedCallsList]);

//   const handleSearch = useCallback(value => {
//     setSearch(value);
//     setPage(0);
//   }, []);

//   const handlePageChange = useCallback(newPage => {
//     setPage(newPage);
//   }, []);

//   const handlePageSizeChange = useCallback(newPageSize => {
//     setPageSize(newPageSize);
//     setPage(0);
//   }, []);

//   const handleSort = useCallback(
//     columnId => {
//       const isAsc = sortColumn === columnId && sortDirection === 'asc';
//       setSortDirection(isAsc ? 'desc' : 'asc');
//       setSortColumn(columnId);
//       setPage(0);
//     },
//     [sortColumn, sortDirection],
//   );

//   return (
//     <CustomTable
//       columns={flaggedColumns}
//       data={flaggedCallsList}
//       actionButtons={[
//         {
//           name: 'ViewButton',
//           component: ViewButton,
//         },
//       ]}
//       title="Flagged Calls"
//       loading={loading}
//       pagination
//       pageSize={pageSize}
//       totalCount={totalCount}
//       page={page}
//       allowRowClick={false}
//       onPageChange={handlePageChange}
//       onPageSizeChange={handlePageSizeChange}
//       onSearch={handleSearch}
//       onSort={handleSort}
//       sortColumn={sortColumn}
//       sortDirection={sortDirection}
//     />
//   );
// });

export const NeedsAttentionTab = withStyles(styles)(({ classes }) => {
  const [loading, setLoading] = useState(false);
  const [needsAttentionCallsList, setNeedsAttentionCallsList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');

  const history = useHistory();

  const fetchNeedsAttentionCallsList = useCallback(
    async (fetchPage, fetchPageSize, fetchSearch, fetchSortColumn, fetchSortDirection) => {
      setLoading(true);
      try {
        const data = await getVapiFailedCalls({
          page: fetchPage + 1,
          pageSize: fetchPageSize,
          search: fetchSearch,
          sort: fetchSortColumn,
          order: fetchSortDirection,
        });
        setNeedsAttentionCallsList(data.results || []);
        setTotalCount(data.totalCount || 0);
      } catch (error) {
        console.error('Error fetching needs attention calls list:', error);
        setNeedsAttentionCallsList([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const ViewButton = ({ row }) => (
    <Button
      variant="contained"
      size="small"
      className={`${classes.actionButton} ${classes.viewButton}`}
      onClick={() => history.push(`/call-manager/analysis/${row.providerCallId}`)}
    >
      VIEW
    </Button>
  );

  useEffect(() => {
    fetchNeedsAttentionCallsList(page, pageSize, search, sortColumn, sortDirection);
  }, [page, pageSize, search, sortColumn, sortDirection, fetchNeedsAttentionCallsList]);

  const handleSearch = useCallback(value => {
    setSearch(value);
    setPage(0);
  }, []);

  const handlePageChange = useCallback(newPage => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback(newPageSize => {
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  const handleSort = useCallback(
    columnId => {
      const isAsc = sortColumn === columnId && sortDirection === 'asc';
      setSortDirection(isAsc ? 'desc' : 'asc');
      setSortColumn(columnId);
      setPage(0);
    },
    [sortColumn, sortDirection],
  );

  const handleExportNeedsAttention = async () => {
    setExporting(true);
    try {
      const start = exportStartDate ? new Date(exportStartDate).toISOString() : null;
      const end = exportEndDate ? new Date(exportEndDate + 'T23:59:59').toISOString() : null;
      await exportNeedsAttention(start, end);
      setExportDialogOpen(false);
    } catch (error) {
      if (error.message === 'No records to export') alert('No records found for the selected date range.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
    <CustomTable
      columns={needsAttentionColumns}
      data={needsAttentionCallsList}
      actionButtons={[
        {
          name: 'ViewButton',
          component: ViewButton,
        },
      ]}
      title="Needs Attention"
      loading={loading}
      pagination
      pageSize={pageSize}
      totalCount={totalCount}
      page={page}
      allowRowClick={false}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onSearch={handleSearch}
      onSort={handleSort}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      headerActions={
        <Button
          variant="contained"
          size="small"
          className={classes.exportButton}
          startIcon={<GetAppIcon />}
          onClick={() => setExportDialogOpen(true)}
        >
          Export
        </Button>
      }
    />
    <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
      <DialogTitle>Export Needs Attention</DialogTitle>
      <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 300 }}>
        <TextField
          label="Start Date"
          type="date"
          value={exportStartDate}
          onChange={e => setExportStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="End Date"
          type="date"
          value={exportEndDate}
          onChange={e => setExportEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <Typography variant="caption" color="textSecondary">
          Leave both blank to export all records.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleExportNeedsAttention} color="primary" variant="contained" disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
});

const metricsStyles = theme => ({
  root: {
    padding: '20px 24px 24px',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    backgroundColor: '#f4f6f8',
    minHeight: '100%',
  },
  filtersRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 24,
    backgroundColor: '#fff',
    padding: '14px 20px',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  clearLink: {
    color: '#6c757d',
    textTransform: 'none',
    fontSize: '0.8rem',
    minWidth: 'auto',
    padding: '4px 8px',
    marginLeft: 4,
  },
  summaryGrid: {
    marginBottom: 24,
    justifyContent: 'center',
  },
  summaryCard: {
    textAlign: 'center',
    padding: '10px 8px !important',
    '&:last-child': { paddingBottom: '10px !important' },
    borderRadius: 6,
  },
  cardTotal: {
    borderLeft: '3px solid #1565c0',
    backgroundColor: '#fff',
  },
  cardSuccess: {
    borderLeft: '3px solid #2e7d32',
    backgroundColor: '#fff',
  },
  cardFailed: {
    borderLeft: '3px solid #c62828',
    backgroundColor: '#fff',
  },
  cardRate: {
    borderLeft: '3px solid #6a1b9a',
    backgroundColor: '#fff',
  },
  cardDuration: {
    borderLeft: '3px solid #00838f',
    backgroundColor: '#fff',
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  valTotal: { color: '#1565c0' },
  valSuccess: { color: '#2e7d32' },
  valFailed: { color: '#c62828' },
  valRate: { color: '#6a1b9a' },
  valDuration: { color: '#00838f' },
  summaryLabel: {
    fontSize: '0.7rem',
    color: '#78909c',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginTop: 6,
    fontWeight: 500,
  },
  tablesRow: {
    display: 'flex',
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  tablePanel: {
    flex: '1 1 0%',
    minWidth: 0,
    overflow: 'hidden',
    '&:first-child': {
      borderRight: '1px solid #dee2e6',
    },
    '& table': {
      minWidth: 'unset !important',
    },
    '& > div > div': {
      maxHeight: '400px !important',
      overflowY: 'auto',
    },
  },
  tablePanelHeader: {
    padding: '14px 16px 10px',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: '#343a40',
    margin: 0,
  },
});

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const byClientColumns = [
  { id: 'clientName', label: 'Client' },
  { id: 'total', label: 'Total' },
  { id: 'success', label: 'Success' },
  { id: 'failed', label: 'Failed' },
  { id: 'successRate', label: 'Success\u00A0%' },
];

const byMonthColumns = [
  { id: 'monthLabel', label: 'Month' },
  { id: 'total', label: 'Total' },
  { id: 'success', label: 'Success' },
  { id: 'failed', label: 'Failed' },
  { id: 'successRate', label: 'Success\u00A0%' },
];

export const CallMetricsTab = withStyles(metricsStyles)(({ classes }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(getTodayDateString);
  const [endDate, setEndDate] = useState(getTodayDateString);

  const fetchMetrics = useCallback(async (start, end) => {
    setLoading(true);
    try {
      const s = start ? new Date(start).toISOString() : null;
      const e = end ? new Date(end + 'T23:59:59').toISOString() : null;
      const data = await getCallMetrics(s, e);
      setMetrics(data);
    } catch (err) {
      console.error('Error fetching call metrics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const today = getTodayDateString();
    fetchMetrics(today, today);
  }, [fetchMetrics]);

  const handleApply = () => fetchMetrics(startDate, endDate);

  const handleClear = () => {
    const today = getTodayDateString();
    setStartDate(today);
    setEndDate(today);
    fetchMetrics(today, today);
  };

  const fmtDuration = secs => {
    if (!secs) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const fmtPct = (num, denom) => (denom ? ((num / denom) * 100).toFixed(1) + '%' : '0%');

  const fmtMonth = raw => {
    if (!raw) return '';
    const [y, m] = raw.split('-');
    return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
  };

  const clientData = (metrics?.byClient || []).map(r => ({
    ...r,
    successRate: fmtPct(r.success, r.total),
  }));

  const monthData = (metrics?.byMonth || []).map(r => ({
    ...r,
    monthLabel: fmtMonth(r.monthLabel),
    successRate: fmtPct(r.success, r.total),
  }));

  const summary = metrics?.summary || {};
  const today = getTodayDateString();
  const hasFilter = startDate !== today || endDate !== today;

  const cards = [
    { label: 'Total Calls', value: summary.totalCalls ?? 0, cardCls: classes.cardTotal, valCls: classes.valTotal },
    { label: 'Successful', value: summary.successful ?? 0, cardCls: classes.cardSuccess, valCls: classes.valSuccess },
    { label: 'Failed', value: summary.failed ?? 0, cardCls: classes.cardFailed, valCls: classes.valFailed },
    { label: 'Success Rate', value: fmtPct(summary.successful, summary.totalCalls), cardCls: classes.cardRate, valCls: classes.valRate },
    { label: 'Avg Duration', value: fmtDuration(summary.avgDuration), cardCls: classes.cardDuration, valCls: classes.valDuration },
  ];

  return (
    <div className={classes.root}>
      <div className={classes.filtersRow}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          variant="outlined"
          style={{ marginRight: 16 }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          variant="outlined"
          style={{ marginRight: 16 }}
        />
        <Button variant="contained" color="primary" size="small" onClick={handleApply} disabled={loading}>
          {loading ? 'Loading...' : 'Apply'}
        </Button>
        {hasFilter && (
          <Button className={classes.clearLink} size="small" onClick={handleClear}>
            Clear
          </Button>
        )}
      </div>

      {loading && !metrics ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} className={classes.summaryGrid}>
            {cards.map(card => (
              <Grid item xs={6} sm={4} md key={card.label} style={{ maxWidth: '20%', flexBasis: '20%' }}>
                <Card elevation={0} className={card.cardCls}>
                  <CardContent className={classes.summaryCard}>
                    <Typography className={`${classes.summaryValue} ${card.valCls}`}>{card.value}</Typography>
                    <Typography className={classes.summaryLabel}>{card.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <div className={classes.tablesRow}>
            <div className={classes.tablePanel}>
              <div className={classes.tablePanelHeader}>
                <Typography className={classes.sectionTitle}>By Client</Typography>
              </div>
              <CustomTable
                columns={byClientColumns}
                data={clientData}
                title=""
                allowRowClick={false}
                allowSearch={false}
                loading={loading}
              />
            </div>
            <div className={classes.tablePanel}>
              <div className={classes.tablePanelHeader}>
                <Typography className={classes.sectionTitle}>By Month</Typography>
              </div>
              <CustomTable
                columns={byMonthColumns}
                data={monthData}
                title=""
                allowRowClick={false}
                allowSearch={false}
                loading={loading}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
});
